import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import StatusBanner from "../components/StatusBanner";

const BillingDiag = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [monitorData, setMonitorData] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminToken, setAdminToken] = useState(
    localStorage.getItem("admin_alerts_jwt") || "",
  );

  useEffect(() => {
    if (activeTab === "overview") {
      fetchMonitorData();
    } else if (activeTab === "alerts") {
      fetchAlertsData();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchMonitorData();

    // Auto-refresh every 5 minutes for overview
    const interval = setInterval(() => {
      if (activeTab === "overview") {
        fetchMonitorData();
      }
    }, 300000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchMonitorData = async () => {
    try {
      setLoading(activeTab === "overview");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/monitor`,
      );

      if (response.ok) {
        const data = await response.json();
        setMonitorData(data);
        setError(null);
      } else {
        setError("Failed to fetch monitoring data");
      }
    } catch (err) {
      setError("Connection error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertsData = async () => {
    if (!adminToken) return;

    try {
      setAlertsLoading(true);

      // Fetch alerts state
      const stateResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alerts/state`,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        },
      );

      if (stateResponse.ok) {
        const stateData = await stateResponse.json();
        setAlertsData(stateData);
      }

      // Fetch recent alerts
      const recentResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alerts/recent?limit=50`,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        },
      );

      if (recentResponse.ok) {
        const recentData = await recentResponse.json();
        setRecentAlerts(recentData.alerts || []);
      }
    } catch (err) {
      console.error("Failed to fetch alerts data:", err);
    } finally {
      setAlertsLoading(false);
    }
  };

  const runSyntheticMonitor = async () => {
    if (!adminToken) {
      alert("Admin token required");
      return;
    }

    try {
      setAlertsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/monitor/synthetic`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const result = await response.json();

        // Format classification label based on mode and result
        let classificationLabel = result.classification;
        if (result.classification === "timeout") {
          const isTestMode = result.mode === "test";
          classificationLabel = isTestMode
            ? "Timeout (test) — informational"
            : "Timeout — investigate";
        }

        alert(
          `Synthetic monitor completed:\n- Stripe API: ${result.stripe_api_ok ? "✓" : "✗"}\n- Portal: ${result.portal_check}\n- Webhook: ${result.webhook_echo_ok ? "✓" : "✗"}\n- Classification: ${classificationLabel}\n- Duration: ${result.duration_ms}ms\n- Mode: ${result.mode}`,
        );
        fetchAlertsData(); // Refresh alerts
      } else {
        alert("Synthetic monitor failed");
      }
    } catch (err) {
      alert("Error running synthetic monitor");
    } finally {
      setAlertsLoading(false);
    }
  };

  const saveAdminToken = () => {
    localStorage.setItem("admin_alerts_jwt", adminToken);
    fetchAlertsData();
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return "Never";
    return new Date(isoString).toLocaleString();
  };

  const getStatusColor = (rate) => {
    if (rate >= 0.95) return "text-green-600";
    if (rate >= 0.85) return "text-yellow-600";
    return "text-red-600";
  };

  if (loading && !monitorData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading billing monitor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              Monitor Unavailable
            </h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchMonitorData}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">
              Billing Monitor
            </h1>
            <div className="flex items-center space-x-4">
              {activeTab === "overview" && (
                <>
                  <span className="text-sm text-gray-500">
                    Last updated: {formatTimestamp(monitorData?.generated_at)}
                  </span>
                  <button
                    onClick={fetchMonitorData}
                    className="bg-orange-600 text-white px-4 py-2 rounded text-sm hover:bg-orange-700"
                    disabled={loading}
                  >
                    {loading ? "Refreshing..." : "Refresh"}
                  </button>
                </>
              )}
              {activeTab === "alerts" && (
                <button
                  onClick={runSyntheticMonitor}
                  className="bg-orange-600 text-white px-4 py-2 rounded text-sm hover:bg-orange-700"
                  disabled={alertsLoading || !adminToken}
                >
                  {alertsLoading ? "Running..." : "Run Synthetic Now"}
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/diag/leads"
                className="text-orange-600 hover:text-orange-700"
              >
                ← Back to Diagnostics
              </Link>
              <span className="text-gray-400">•</span>
              <span className="text-sm text-gray-600">
                {activeTab === "overview"
                  ? "24-hour activity summary"
                  : "Alerts & monitoring"}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("overview")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("alerts")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "alerts"
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Alerts
              </button>
            </nav>
          </div>
        </div>

        {/* Status Banner */}
        <StatusBanner className="mb-6" />

        {/* Tab Content */}
        {activeTab === "overview" && (
          <>
            {/* Monitor Tiles */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Subscriptions Tile */}
              <div
                className="bg-white rounded-lg shadow p-6"
                data-testid="subscriptions-tile"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Subscriptions
                  </h3>
                  <div className="text-2xl">📊</div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Active</span>
                    <span className="font-semibold text-green-600">
                      {monitorData?.subscriptions?.active || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Trialing</span>
                    <span className="font-semibold text-blue-600">
                      {monitorData?.subscriptions?.trialing || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Past Due</span>
                    <span className="font-semibold text-yellow-600">
                      {monitorData?.subscriptions?.past_due || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Canceled</span>
                    <span className="font-semibold text-red-600">
                      {monitorData?.subscriptions?.canceled || 0}
                    </span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span className="text-sm text-gray-900">Total</span>
                    <span>{monitorData?.subscriptions?.total || 0}</span>
                  </div>
                </div>
              </div>

              {/* Invoices Tile */}
              <div
                className="bg-white rounded-lg shadow p-6"
                data-testid="invoices-tile"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Invoices
                  </h3>
                  <div className="text-2xl">📋</div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Succeeded</span>
                    <span className="font-semibold text-green-600">
                      {monitorData?.invoices?.succeeded || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Failed</span>
                    <span className="font-semibold text-red-600">
                      {monitorData?.invoices?.failed || 0}
                    </span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span className="text-sm text-gray-900">Total</span>
                    <span>{monitorData?.invoices?.total || 0}</span>
                  </div>

                  {monitorData?.invoices?.total > 0 && (
                    <div className="mt-3 text-center">
                      <span className="text-xs text-gray-500">
                        Success Rate:
                      </span>
                      <div
                        className={`text-lg font-bold ${
                          (monitorData?.invoices?.succeeded || 0) /
                            monitorData?.invoices?.total >=
                          0.9
                            ? "text-green-600"
                            : "text-yellow-600"
                        }`}
                      >
                        {Math.round(
                          ((monitorData?.invoices?.succeeded || 0) /
                            monitorData?.invoices?.total) *
                            100,
                        )}
                        %
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Webhooks Tile */}
              <div
                className="bg-white rounded-lg shadow p-6"
                data-testid="webhooks-tile"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Webhooks
                  </h3>
                  <div className="text-2xl">🔗</div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Events</span>
                    <span className="font-semibold">
                      {monitorData?.webhooks?.events_total || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Delivery Rate</span>
                    <span
                      className={`font-semibold ${getStatusColor(monitorData?.webhooks?.delivery_success_rate || 0)}`}
                    >
                      {Math.round(
                        (monitorData?.webhooks?.delivery_success_rate || 0) *
                          100,
                      )}
                      %
                    </span>
                  </div>
                  <hr className="my-2" />
                  <div>
                    <span className="text-xs text-gray-500">Last Event:</span>
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {monitorData?.webhooks?.last_event_type || "None"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatTimestamp(monitorData?.webhooks?.last_event_ts)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Failures Tile */}
              <div
                className="bg-white rounded-lg shadow p-6"
                data-testid="failures-tile"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Failures
                  </h3>
                  <div className="text-2xl">⚠️</div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      Failed Invoices
                    </span>
                    <span className="font-semibold text-red-600">
                      {monitorData?.failures?.failed_invoices || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      Pending Webhooks
                    </span>
                    <span className="font-semibold text-yellow-600">
                      {monitorData?.failures?.pending_webhooks || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Past Due Subs</span>
                    <span className="font-semibold text-orange-600">
                      {monitorData?.failures?.past_due_subs || 0}
                    </span>
                  </div>
                  <hr className="my-2" />
                  <div className="text-center">
                    {(monitorData?.failures?.failed_invoices || 0) +
                      (monitorData?.failures?.pending_webhooks || 0) +
                      (monitorData?.failures?.past_due_subs || 0) ===
                    0 ? (
                      <span className="text-sm font-semibold text-green-600">
                        All Good ✓
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-red-600">
                        {(monitorData?.failures?.failed_invoices || 0) +
                          (monitorData?.failures?.pending_webhooks || 0) +
                          (monitorData?.failures?.past_due_subs || 0)}{" "}
                        Issues
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Monitor Details
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Data Period
                  </h4>
                  <p className="text-sm text-gray-600">
                    Last 24 hours • {monitorData?.period}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-refreshes every 5 minutes
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    System Status
                  </h4>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">
                      Live Mode Active
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Monitoring Stripe production environment
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Alerts Tab */}
        {activeTab === "alerts" && (
          <>
            {/* Admin Token Configuration */}
            {!adminToken && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                  Admin Authentication Required
                </h3>
                <p className="text-yellow-700 mb-4">
                  Enter the ADMIN_ALERTS_JWT token to access alerts management.
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="password"
                    value={adminToken}
                    onChange={(e) => setAdminToken(e.target.value)}
                    placeholder="Enter admin token..."
                    className="flex-1 border border-yellow-300 rounded px-3 py-2"
                  />
                  <button
                    onClick={saveAdminToken}
                    className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}

            {adminToken && (
              <>
                {/* Current Trigger States */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Current Trigger States
                  </h3>
                  {alertsLoading ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                    </div>
                  ) : alertsData ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                      {Object.entries(alertsData.trigger_states || {}).map(
                        ([trigger, state]) => (
                          <div key={trigger} className="text-center">
                            <div
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                state.status === "ALERT"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {state.status === "ALERT" ? "🚨" : "✅"}{" "}
                              {state.status}
                            </div>
                            <div className="text-xs text-gray-600 mt-1 capitalize">
                              {trigger.replace("_", " ")}
                            </div>
                            <div className="text-xs text-gray-500">
                              Count: {state.count}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500">
                      Failed to load trigger states
                    </p>
                  )}

                  {alertsData && (
                    <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>
                          Last Check:{" "}
                          {alertsData.last_check
                            ? new Date(alertsData.last_check).toLocaleString()
                            : "Never"}
                        </span>
                        <span>
                          Next Check: {alertsData.next_check_in_min} min
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notification Configuration Status */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Notification Channels
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-600">Slack</span>
                      <span className="text-xs text-gray-500">
                        (configured via SLACK_WEBHOOK_URL)
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-600">Email</span>
                      <span className="text-xs text-gray-500">
                        (configured via ALERT_EMAIL)
                      </span>
                    </div>
                  </div>

                  {/* Helper texts for common issues */}
                  <div className="mt-4 space-y-2">
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-sm text-blue-800">
                        💡 <strong>Portal Test in Live Mode:</strong> Set a
                        Stripe customer for portal test via portal_customer_id
                        in request body (admin-only).
                      </p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded p-3">
                      <p className="text-sm text-amber-800">
                        📧 <strong>Notification Setup:</strong> Configure
                        SLACK_WEBHOOK_URL and/or ALERT_EMAIL environment
                        variables to enable alerts.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recent Alerts */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Recent Alerts
                  </h3>
                  {recentAlerts.length > 0 ? (
                    <div className="space-y-3">
                      {recentAlerts.slice(0, 10).map((alert) => (
                        <div
                          key={alert.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded"
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  alert.severity === "high"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {alert.severity}
                              </span>
                              <span className="text-sm font-medium text-gray-900 capitalize">
                                {alert.type?.replace("_", " ")}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {alert.summary}
                            </p>
                          </div>
                          <div className="text-xs text-gray-500">
                            {alert.created_at
                              ? new Date(alert.created_at).toLocaleString()
                              : "N/A"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No recent alerts
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BillingDiag;
